import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactApexChart from 'react-apexcharts';
import useMediaQuery from '@mui/material/useMediaQuery';
import TabsComponent from '@shared/ui/TabsComponent';
import {
  getSpentChartOptions,
  mapPeriodDataToSeries,
} from '@utils/charts-helpers';
import { formatBalanceUi } from '@utils/balance-helpers';
import { theme } from '@utils/libs/theme';

const CURRENCY_OPTIONS = [
  { label: 'EUR', value: 'EUR' },
  { label: 'USD', value: 'USD' },
];

export default function SpentChart({ statistics }) {
  const { t } = useTranslation();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const PERIOD_OPTIONS = [
    { label: t('today'), value: 'today' },
    { label: t('week'), value: 'week' },
    { label: t('month'), value: 'month' },
    { label: t('year'), value: 'year' },
  ];

  const [tab, setTab] = useState('week');
  const [selectedCurrency, setSelectedCurrency] = useState('USD');

  const currentPeriodBlock = statistics?.[selectedCurrency]?.[tab];

  const { categories, series } = useMemo(
    () => mapPeriodDataToSeries(currentPeriodBlock, tab),
    [currentPeriodBlock, tab],
  );

  const options = useMemo(
    () => getSpentChartOptions(tab, categories, selectedCurrency),
    [tab, categories, selectedCurrency],
  );

  const POINT_PX = isMobile ? 50 : 70;
  const MIN_PX = 500;
  const chartWidth = Math.max(MIN_PX, categories.length * POINT_PX);

  return (
    <div className="spentChart">
      <div className="spentChart__heading">
        <div>
          <p className="body-18m">{t('spent')}</p>
          <p
            className="title-h1r"
            dangerouslySetInnerHTML={{
              __html: formatBalanceUi(
                currentPeriodBlock?.sum_spent ?? 0,
                selectedCurrency,
                null,
                true,
              ),
            }}
          ></p>
        </div>
        <div className="d-flex form-group">
          <TabsComponent
            isGroup
            options={CURRENCY_OPTIONS}
            value={selectedCurrency}
            setValue={setSelectedCurrency}
          />
          <TabsComponent
            isGroup
            options={PERIOD_OPTIONS}
            value={tab}
            setValue={setTab}
          />
        </div>
      </div>
      <div className="spentChart__chart">
        <div className="chartScroll">
          <div className="chartScroll__inner" style={{ width: chartWidth }}>
            <ReactApexChart
              options={options}
              series={series}
              type="bar"
              height={360}
            />
          </div>
        </div>
        <div className="spentChart__legends">
          <div>{t('charged')}</div>
          <div>{t('hold')}</div>
        </div>
      </div>
    </div>
  );
}
